import { useState, useEffect } from 'react';
import { apiService } from '../../lib/api';
import { useNotifications } from '../../hooks/useNotifications';
// import ConfirmModal from '../UI/ConfirmModal';

interface Webhook {
  id: number;
  name: string;
  webhookUrl: string;
  enabled: number;
  createdAt: string;
  updatedAt: string;
}

export default function ReconstructionWebhooks() {
  const { showSuccess, showError } = useNotifications();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [webhookToDelete, setWebhookToDelete] = useState<Webhook | null>(null);
  // const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    webhookUrl: '',
    apiKey: '',
    enabled: true,
  });

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      const response = await apiService.request<any>('/api/v1/admin/reconstruction_webhooks');
      if (response.success) {
        setWebhooks(response.data || []);
      }
    } catch (error: any) {
      console.error('Failed to load webhooks:', error);
      showError('Load Failed', error.message || 'Failed to load webhooks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiService.request<any>('/api/v1/admin/reconstruction_webhooks', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      if (response.success) {
        showSuccess('Webhook Created', 'Webhook has been created successfully');
        setShowAddModal(false);
        resetForm();
        loadWebhooks();
      }
    } catch (error: any) {
      showError('Creation Failed', error.message || 'Failed to create webhook');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingWebhook) return;

    try {
      const response = await apiService.request<any>(`/api/v1/admin/reconstruction_webhooks/${editingWebhook.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData),
      });

      if (response.success) {
        showSuccess('Webhook Updated', 'Webhook has been updated successfully');
        setEditingWebhook(null);
        resetForm();
        loadWebhooks();
      }
    } catch (error: any) {
      showError('Update Failed', error.message || 'Failed to update webhook');
    }
  };

  // Unused handler - kept for potential future use
  // const handleDelete = async () => {
  //   if (!webhookToDelete) return;
  //   setIsDeleting(true);
  //   try {
  //     const response = await apiService.request<any>(`/api/v1/admin/reconstruction_webhooks/${webhookToDelete.id}`, {
  //       method: 'DELETE',
  //     });

  //     if (response.success) {
  //       showSuccess('Webhook Deleted', 'Webhook has been deleted successfully');
  //       loadWebhooks();
  //     }
  //   } catch (error: any) {
  //     showError('Deletion Failed', error.message || 'Failed to delete webhook');
  //   } finally {
  //     setIsDeleting(false);
  //     setShowDeleteConfirm(false);
  //     setWebhookToDelete(null);
  //   }
  // };

  const openDeleteConfirm = (webhook: Webhook) => {
    setWebhookToDelete(webhook);
    setShowDeleteConfirm(true);
  };

  const handleToggleEnabled = async (webhook: Webhook) => {
    try {
      const response = await apiService.request<any>(`/api/v1/admin/reconstruction_webhooks/${webhook.id}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !webhook.enabled }),
      });

      if (response.success) {
        showSuccess('Status Updated', `Webhook ${webhook.enabled ? 'disabled' : 'enabled'} successfully`);
        loadWebhooks();
      }
    } catch (error: any) {
      showError('Update Failed', error.message || 'Failed to update webhook status');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', webhookUrl: '', apiKey: '', enabled: true });
  };

  const startEdit = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      name: webhook.name,
      webhookUrl: webhook.webhookUrl,
      apiKey: '',
      enabled: webhook.enabled === 1,
    });
  };

  const cancelEdit = () => {
    setEditingWebhook(null);
    setShowAddModal(false);
    resetForm();
  };

  if (isLoading) {
    return <div className="p-6">Loading webhooks...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold theme-text-primary">Reconstruction Webhooks</h2>
          <p className="theme-text-secondary mt-1">
            Configure multiple applications to receive reconstruction notifications
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-healthy-green text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          + Add Webhook
        </button>
      </div>

      {/* Webhooks List */}
      <div className="space-y-4">
        {webhooks.length === 0 ? (
          <div className="text-center py-12 theme-surface rounded-lg border theme-border-primary">
            <p className="theme-text-secondary">No webhooks configured</p>
            <p className="theme-text-tertiary text-sm mt-2">Click "Add Webhook" to create your first webhook</p>
          </div>
        ) : (
          webhooks.map((webhook) => (
            <div key={webhook.id} className="theme-surface rounded-lg border theme-border-primary p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold theme-text-primary">{webhook.name}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        webhook.enabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {webhook.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="theme-text-secondary text-sm mt-1 font-mono">{webhook.webhookUrl}</p>
                  <p className="theme-text-tertiary text-xs mt-1">
                    Created: {new Date(webhook.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleEnabled(webhook)}
                    className="px-3 py-1 text-sm theme-text-secondary hover:theme-text-primary transition-colors"
                  >
                    {webhook.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => startEdit(webhook)}
                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => openDeleteConfirm(webhook)}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingWebhook) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="theme-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
            <h3 className="text-xl font-bold theme-text-primary mb-4">
              {editingWebhook ? 'Edit Webhook' : 'Add New Webhook'}
            </h3>

            <form onSubmit={editingWebhook ? handleUpdate : handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  Webhook Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 theme-input rounded-lg"
                  placeholder="e.g., Production App, Staging Server"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  Webhook URL *
                </label>
                <input
                  type="url"
                  value={formData.webhookUrl}
                  onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                  className="w-full px-3 py-2 theme-input rounded-lg"
                  placeholder="https://example.com/api/webhooks/reconstruction"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">
                  API Key (Optional)
                </label>
                <input
                  type="text"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  className="w-full px-3 py-2 theme-input rounded-lg"
                  placeholder="Enter API key if required by the webhook endpoint"
                />
                <p className="text-xs theme-text-tertiary mt-1">
                  If provided, will be sent as X-API-Key header
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="enabled" className="text-sm theme-text-primary">
                  Enable this webhook
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 theme-text-secondary hover:theme-text-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-healthy-green text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {editingWebhook ? 'Update Webhook' : 'Create Webhook'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-6 theme-info-panel rounded-lg p-4">
        <h4 className="font-semibold mb-2">📋 How it works</h4>
        <ul className="text-sm space-y-1">
          <li>• Multiple applications can receive reconstruction notifications simultaneously</li>
          <li>• Each webhook receives the EXACT payload from GPS Planning App (no modifications)</li>
          <li>• Webhooks are called in parallel for best performance</li>
          <li>• Failed webhooks won't affect others - each is independent</li>
          <li>• Enable/disable webhooks without deleting them</li>
        </ul>
      </div>
    </div>
  );
}
